import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useFragment } from "react-relay";

import { punchPassesByTargetIdAtom } from "../../../../atoms/stores";
import { useGetBookingCharge } from "../useGetBookingCharge/useGetBookingCharge";
import { useGetBookingFees } from "../useGetBookingFees";
import { useIsViewMoreVisible_Booking$key } from "./__generated__/useIsViewMoreVisible_Booking.graphql";
import {
  getIsViewMoreVisible,
  hasPunchPassesForTarget,
  hasValidLineItemsForRounds,
} from "./getIsViewMoreVisible";

export const useIsViewMoreVisible = (
  bookingRef: useIsViewMoreVisible_Booking$key | null
): boolean => {
  const data = useFragment(useIsViewMoreVisibleFragmentString, bookingRef);
  const punchPassState = useAtomValue(punchPassesByTargetIdAtom);
  const { bookingFees } = useGetBookingFees({
    bookingFragmentRef: data ?? null,
  });
  const { bookingChargeGroups } = useGetBookingCharge({
    bookingFragmentRef: data ?? null,
  });

  const facility = data?.startSlot?.agenda?.facility;
  const hidePriceDetails = Boolean(facility?.club?.supportsPrivate);
  const isDurationBasedFacility = Boolean(
    facility?.type && facility.type !== "GOLF_COURSE"
  );
  const currentRoundIds =
    data?.rounds?.edges
      ?.map((edge) => edge?.node?.id)
      .filter((id): id is string => Boolean(id)) ?? [];

  return getIsViewMoreVisible({
    hidePriceDetails,
    isDurationBasedFacility,
    anyBookingFees: bookingFees.length > 0,
    hasRoundLineItems: hasValidLineItemsForRounds(
      data?.orders?.edges,
      currentRoundIds
    ),
    hasBookingChargeGroups: bookingChargeGroups.length > 0,
    hasRoundPunchPasses: currentRoundIds.some((roundId) =>
      hasPunchPassesForTarget(punchPassState, roundId)
    ),
    hasBookingPunchPasses: hasPunchPassesForTarget(punchPassState, data?.id),
  });
};

const useIsViewMoreVisibleFragmentString = graphql`
  fragment useIsViewMoreVisible_Booking on Booking {
    id
    startSlot {
      agenda {
        facility {
          type
          club {
            supportsPrivate
          }
        }
      }
    }
    rounds {
      edges {
        node {
          id
        }
      }
    }
    orders {
      edges {
        node {
          lineItemGroups {
            round {
              id
            }
            lineItems {
              id
              __typename
              name
              price
              type
            }
          }
        }
      }
    }
    ...useGetBookingFees_Booking
    ...useGetBookingCharge_Booking
  }
`;
